import { computeBasicPlanPricing, computeOneTimePricing } from "../ChoosePlanCarePlus/pricing";
import { WlReorderSubscriptionOffer } from "./reorderTypes";
import { offersSubscriptionOnReorderScreen, WlReorderVariant } from "../../utils/wlReorderSubscriptionExperiment";

// Reorder screen prices (also the tracking "Medication Price").

/** Price on the legacy reorder product card. */
export const calcLegacyProductPrice = (productItem: any, onlyPrescription: boolean, country: string): number => {
  if (!onlyPrescription && productItem.is_cannabis) {
    return productItem.product_item_official_price || 0;
  }

  if (country.toLowerCase() !== "fr") {
    return onlyPrescription
      ? productItem.treatment_fee || 0
      : (productItem.product_item_official_price || 0) + (productItem.product_item_service_fee || 0);
  }

  return onlyPrescription ? productItem.treatment_fee || 0 : productItem.product_item_price || 0;
};

/** Discount of the picked legacy plan. */
export const getLegacyPlanDiscount = (reorderData: any, selectedPlanID: number | string | null): number => {
  const productItems = reorderData.orderProductItems;
  if (productItems.length > 1 || !selectedPlanID || selectedPlanID === "one-time") return 0;
  const plans = productItems[0]?.subscriptionPlans?.[productItems[0].product_item_id] ?? [];
  const selectedPlan = plans.find((plan: any) => plan.product_plan_id === selectedPlanID);
  return selectedPlan?.discount ?? 0;
};

/** Legacy screen total: products less the plan discount. */
export const getLegacyReorderTotal = (reorderData: any, country: string, selectedPlanID: number | string | null): number => {
  const productsTotal = reorderData.orderProductItems.reduce(
    (sum: number, productItem: any) => sum + calcLegacyProductPrice(productItem, reorderData.isOnlyPrescription, country),
    0
  );
  return Math.max(0, productsTotal - getLegacyPlanDiscount(reorderData, selectedPlanID));
};

/** The plan DrugSubscription preselects on mount: shortest interval, DE single-product only. */
export const getLegacyPreselectedPlanID = (reorderData: any, country: string): number | null => {
  const productItems = reorderData.orderProductItems;
  if (productItems.length > 1 || country.toLowerCase() !== "de") return null;
  const plans: any[] = productItems[0]?.subscriptionPlans?.[productItems[0].product_item_id] ?? [];
  if (!plans.length) return null;
  const shortest = plans.reduce((min, plan) => (plan.interval < min.interval ? plan : min));
  return shortest.product_plan_id ?? null;
};

export const getReorderMedicationPrice = (reorderData: any): number =>
  Number(reorderData.orderProductItems[0]?.product_item_official_price) || 0;

/** Care+ and one-time prices of the WL reorder screen. */
export const getWlReorderPricing = (offer: WlReorderSubscriptionOffer, medicationPrice: number) => ({
  plan: computeBasicPlanPricing(offer.duration, medicationPrice),
  oneTime: computeOneTimePricing(offer.productFees, medicationPrice, offer.oneTimeBonus),
});

/** WL test arms B and C. */
export const showsWlReorderOffer = (reorderData: any, variant: WlReorderVariant | null): boolean =>
  Boolean(reorderData.wlSubscriptionOffer) && offersSubscriptionOnReorderScreen(variant);

/** Price of the preselected option, for events sent before the customer picks. */
export const getPreselectedReorderPrice = (reorderData: any, country: string, variant: WlReorderVariant | null): number =>
  showsWlReorderOffer(reorderData, variant)
    ? getWlReorderPricing(reorderData.wlSubscriptionOffer, getReorderMedicationPrice(reorderData)).plan.headerCharged
    : getLegacyReorderTotal(reorderData, country, getLegacyPreselectedPlanID(reorderData, country));
